import { useNavigate } from "@tanstack/react-router";
import { forwardRef } from "react";
import { useObjectRef, usePress } from "react-aria";
import { Button as RACButton } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { StatIndicator } from "@/components/Indicators";
import { Badge, badgeStyles } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ListItem,
  ListItemLeft,
  ListItemRight,
} from "@/components/ui/list-item";
import { Loader } from "@/components/ui/loader";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import type { TAccession } from "@/lib/types";
import { format } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";
import { useNavLocationState } from "@/utils/useNavStack";

export const ReportListItem = forwardRef<
  HTMLDivElement,
  {
    accession: TAccession;
    isActive?: boolean;
    isQueued?: boolean;
    onGenerated?: (ids: number[]) => void;
    className?: string;
  }
>(({ isActive, isQueued, accession, onGenerated, ...props }, fref) => {
  const utils = trpc.useUtils();
  const generate = trpc.reports.generate.useMutation();
  const ref = useObjectRef(fref);
  const navigate = useNavigate();
  const { href, label } = useNavLocationState();

  const { pressProps } = usePress({
    ...props,
    ref,
    onPress() {
      navigate({
        to: "/accessions/$id/report",
        params: { id: accession.id },
        state: {
          href,
          label,
          selected: accession.id,
        },
      });
    },
  });

  const onGenerate = () => {
    generate.mutate(
      { accessionId: accession.id, status: "final" },
      mutationResponse({
        errorTitle: "Error generating report",
        successMessage: (data) => {
          const accessionId = data?.accessionId;
          return `Report generated for accession: ${accessionId}`;
        },
        onSuccess() {
          // Show "Generating" until the row drops from the list. The
          // single-Generate mutation is synchronous (not via worker),
          // so by the time it returns the report row exists and the
          // status cascade has already fired — realtime invalidation
          // is on its way. The marker just smooths the brief gap
          // between "mutation done" and "list refetched".
          onGenerated?.([accession.id]);
          // Belt-and-suspenders: refresh even if SSE is down.
          utils.accession.requiringReports.invalidate();
        },
      }),
    );
  };

  return (
    <ListItem
      {...pressProps}
      ref={ref}
      className={cn(
        props.className,
        "cursor-pointer last:border-b",
        isQueued && "opacity-50",
      )}
    >
      <ListItemLeft className="h-full gap-4 overflow-hidden">
        <RACButton
          className={badgeStyles({
            color: "gray",
            shape: "circle",
            className: "cursor-pointer",
          })}
          onPress={() => {
            navigate({
              to: "/accessions/$id",
              params: { id: accession.id },
              state: {
                href,
                label,
                selected: accession.id,
              },
            });
          }}
        >
          {accession.id}
        </RACButton>
        <Text size="2" truncate>
          {accession.caseId}
        </Text>
        {accession.priority === "STAT" && <StatIndicator />}
      </ListItemLeft>
      <ListItemRight className="gap-4">
        <Badge className="hidden md:inline-flex">{accession.org?.name}</Badge>

        <Text size="2" muted mono className="hidden sm:inline">
          {format(accession.date)}
        </Text>
        {isQueued ? (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Loader size="small" />
            <Text size="2" muted>
              Generating...
            </Text>
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onPress={onGenerate}
            isPending={generate.isPending}
          >
            Generate
          </Button>
        )}
      </ListItemRight>
    </ListItem>
  );
});

export const ReportLoadingItem = forwardRef<HTMLDivElement>((_, ref) => (
  <ListItem ref={ref} className="w-full h-11">
    <ListItemLeft className="gap-3">
      <Skeleton className="w-20" />
      <Skeleton className="w-28" />
    </ListItemLeft>
    <ListItemRight className="gap-4">
      <Skeleton className="w-20 hidden md:block" />
      <Skeleton className="w-28 hidden sm:block" />
      <Skeleton className="w-20 h-6" />
    </ListItemRight>
  </ListItem>
));
